/**
 * EvidenceEditModal Component
 * ORCA360 - Evidence Editing Modal
 *
 * Modal for editing evidence quotes inline
 * Allows instructors to refine AI-generated evidence
 *
 * Sprint 4, Day 3
 */

import { useState, useEffect } from 'react';
import { normalizeTimestamp } from '../lib/timestampUtils';
import { LLMValidationPanel } from './LLMValidationPanel';
import type { Evidence as LLMEvidence } from './LLMValidationPanel';

export type Evidence = LLMEvidence;

export interface EvidenceEditModalProps {
  evidence: Evidence;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedEvidence: Evidence) => void;
  onDelete?: () => void;
  // Optional: enables the compact LLM validation panel inside the modal
  sessionId?: string;
  assessmentId?: string;
  assessmentType?: 'pilot' | 'instructor';
}

export function EvidenceEditModal({
  evidence,
  isOpen,
  onClose,
  onSave,
  onDelete,
  sessionId,
  assessmentId,
  assessmentType,
}: EvidenceEditModalProps) {
  const [editedQuote, setEditedQuote] = useState(evidence.quote);
  const [editedTimestamp, setEditedTimestamp] = useState(evidence.timestamp);
  const [currentEvidence, setCurrentEvidence] = useState(evidence);

  // Evidence object that reflects the user's current (possibly unsaved) edits.
  // This is what we send to the LLM validation API so that validation never
  // discards local changes to quote/timestamp.
  const evidenceForValidation: Evidence = {
    ...currentEvidence,
    quote: editedQuote,
    timestamp: editedTimestamp,
  };

  // Sync currentEvidence when prop changes (e.g. after LLM validation updates it)
  useEffect(() => {
    setCurrentEvidence(evidence);
  }, [evidence]);

  // Update local state when evidence prop changes
  useEffect(() => {
    setEditedQuote(evidence.quote);
    setEditedTimestamp(evidence.timestamp);
  }, [evidence]);

  const buildPayload = (): Evidence => {
    const normalizedTimestamp = normalizeTimestamp(editedTimestamp);
    return {
      ...currentEvidence,
      quote: editedQuote,
      timestamp: normalizedTimestamp,
    };
  };

  const hasUnsavedModalEdits =
    editedQuote !== evidence.quote
    || normalizeTimestamp(editedTimestamp) !== normalizeTimestamp(evidence.timestamp)
    || currentEvidence.llm_decision !== evidence.llm_decision
    || currentEvidence.llm_reasoning !== evidence.llm_reasoning
    || currentEvidence.reclassified_to_competency !== evidence.reclassified_to_competency;

  const requestClose = () => {
    if (hasUnsavedModalEdits) {
      if (!confirm('Discard unsaved evidence edits?')) return;
    }
    onClose();
  };

  const handleSave = () => {
    onSave(buildPayload());
    onClose();
  };

  const handleDelete = () => {
    if (onDelete && confirm('Delete this evidence? This action cannot be undone.')) {
      onDelete();
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      requestClose();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-steel/20">
          <h2 className="text-xl font-bold text-navy">Edit Evidence</h2>
          <p className="text-sm text-steel mt-1">
            Modify the quote or timestamp, then save to update the session draft.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
          {/* Quote Editor */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Evidence Quote
            </label>
            <textarea
              value={editedQuote}
              onChange={(e) => setEditedQuote(e.target.value)}
              className="w-full h-32 px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
              placeholder="Enter evidence quote..."
              autoFocus
            />
            <p className="text-xs text-steel mt-1">
              {editedQuote.length} characters
            </p>
          </div>

          {/* Timestamp Editor */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Timestamp
            </label>
            <input
              type="text"
              value={editedTimestamp}
              onChange={(e) => setEditedTimestamp(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
              placeholder="e.g., 00:03:45 or 00:03:45-00:04:30"
            />
            <p className="text-xs text-steel mt-1">
              Format: HH:MM:SS or HH:MM:SS-HH:MM:SS for time range
            </p>
          </div>

          {/* Validation Status (read-only) */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Validation Status (read-only)
            </label>
            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 text-xs font-medium rounded ${
                  evidence.validation_status === 'VERIFIED' || evidence.validation_status === 'VERBATIM'
                    ? 'text-green-600 bg-green-50'
                    : evidence.validation_status === 'PARAPHRASED'
                    ? 'text-yellow-600 bg-yellow-50'
                    : evidence.validation_status === 'SIMILAR'
                    ? 'text-orange-600 bg-orange-50'
                    : 'text-red-600 bg-red-50'
                }`}
              >
                {evidence.validation_status === 'VERIFIED' && '✓ VERIFIED'}
                {evidence.validation_status === 'VERBATIM' && '✓ VERBATIM'}
                {evidence.validation_status === 'PARAPHRASED' && '⚠ PARAPHRASED'}
                {evidence.validation_status === 'SIMILAR' && '⚠ SIMILAR'}
                {evidence.validation_status === 'UNVERIFIED' && '❌ UNVERIFIED'}
              </span>
              {evidence.confidence && (
                <span className="text-xs text-steel">
                  Confidence: {(evidence.confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <p className="text-xs text-steel mt-2">
              Note: Editing the quote will not re-validate against the transcript.
              Manual edits are assumed to be instructor-verified.
            </p>
          </div>

          {/* LLM Validation Trigger (Tier 4) - Only shown when context props are provided */}
          {sessionId && assessmentId && assessmentType && (
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">
                AI Re-validation (Tier 4)
              </label>
              <LLMValidationPanel
                sessionId={sessionId}
                assessmentId={assessmentId}
                assessmentType={assessmentType}
                evidence={[evidenceForValidation]}
                onComplete={([updated]) => {
                  if (updated) {
                    setCurrentEvidence(updated as Evidence);
                    onSave(updated as Evidence);
                  }
                }}
                compact
              />
            </div>
          )}

          {/* LLM Validation (Tier 4) - Results display */}
          {currentEvidence.llm_decision && (
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">
                AI Competency Mapping Validation (Tier 4)
              </label>
              <div className="bg-steel/5 rounded-md p-4 space-y-3">
                {/* LLM Decision Badge */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-steel">Decision:</span>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded ${
                      currentEvidence.llm_decision === 'ACCEPTED'
                        ? 'text-green-600 bg-green-50 border border-green-200'
                        : currentEvidence.llm_decision === 'REJECTED'
                        ? 'text-red-600 bg-red-50 border border-red-200'
                        : 'text-blue-600 bg-blue-50 border border-blue-200'
                    }`}
                  >
                    {currentEvidence.llm_decision === 'ACCEPTED' && '✓ ACCEPTED'}
                    {currentEvidence.llm_decision === 'REJECTED' && '✗ REJECTED'}
                    {currentEvidence.llm_decision === 'RECLASSIFIED' && '↻ RECLASSIFIED'}
                  </span>
                  {currentEvidence.reclassified_to_competency && (
                    <span className="text-xs text-steel">
                      → Suggested: <span className="font-semibold">{currentEvidence.reclassified_to_competency}</span>
                    </span>
                  )}
                </div>

                {/* LLM Reasoning */}
                {currentEvidence.llm_reasoning && (
                  <div>
                    <span className="text-xs font-medium text-steel block mb-1">AI Reasoning:</span>
                    <p className="text-xs text-navy bg-white p-3 rounded border border-steel/20 leading-relaxed">
                      {currentEvidence.llm_reasoning}
                    </p>
                  </div>
                )}

                <p className="text-xs text-steel italic">
                  AI validation provides an additional quality check but should be reviewed by the instructor.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-steel/20 flex items-center justify-between">
          <div>
            {onDelete && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
              >
                Delete Evidence
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={requestClose}
              className="px-4 py-2 text-sm font-semibold text-steel bg-steel/10 hover:bg-steel/20 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
              title="Save changes (Ctrl+Enter / Cmd+Enter)"
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* Keyboard Shortcuts Help */}
        <div className="px-6 py-2 bg-white text-xs text-steel">
          <span className="font-semibold">Keyboard shortcuts:</span> Esc = Cancel, Ctrl+Enter /
          Cmd+Enter = Save
        </div>
      </div>
    </div>
  );
}


